import React from 'react'
import * as yup from 'yup'
import Form from 'react-formal'
import Moment from 'moment'
import momentLocalizer from 'react-widgets-moment'
import { Flex, Box, Text, Heading } from 'rebass'
import { Link } from 'gatsby'
import { request } from 'graphql-request'
import {
  Label,
  CenteredContentBox,
  Message,
  SubmitButton,
  Input,
  Fieldset,
  TextArea,
  Checkbox,
  Widget
} from './components'
import { trackEvent } from '../../../lib/tracker'
import permissions from '../../../lib/permission/schema'
import permissionsGraphqlMutation from '../../../lib/permission/graphql-mutation'
import PermissionControls from './components/PermissionControls'
import enquiryTypes from './fixtures/enquiry-types'

Moment.locale('en-gb')
momentLocalizer()

const modelSchema = yup.object({
  email: yup
    .string()
    .default('')
    .required('Please enter an email address')
    .email('Please check your email address is correct')
    .max(255, 'Your email is over 255 characters'),
  firstName: yup
    .string()
    .default('')
    .required('Please enter your first name')
    .max(75, 'Your first name is over 75 characters'),
  lastName: yup
    .string()
    .default('')
    .required('Please enter your last name')
    .max(75, 'Your last name is over 75 characters'),
  phone: yup
    .string()
    .transform((value) => (value === '' ? undefined : value))
    .min(11, 'Please check your number')
    .max(16, 'Your number is too long'),
  postcode: yup
    .string()
    .nullable()
    .matches(
      /^[A-Z]{1,2}[0-9]{1,2}[A-Z]? ?[0-9][A-Z]{2}$/i,
      'Invalid postcode'
    ),
  question: yup.string().default('').max(1200, 'Your question is too long'),
  permissions: permissions(['email', 'sms', 'phone']),
  enquiryType: yup.string().oneOf(enquiryTypes).default(enquiryTypes[0]),
  termsAndConditions: yup
    .bool()
    .test(
      'terms-agreed',
      'You must agree to the terms and conditions',
      (v) => !!v
    )
})

Form.addInputTypes(require('react-formal-inputs'))

const submitForm = (data) => {
  const mutation = `mutation {
    createQuestion(questionRequest: {
      firstName: "${data.firstName}",
      lastName: "${data.lastName}",
      phone: "${data.phone}",
      email: "${data.email}",
      postcode: "${data.postcode}",
      question: ${JSON.stringify(data.question)},
      registration: "${data.registration}",
      location: "${data.location}",
      enquiryType: "${data.enquiryType}",
      sourceUrl: "${document.location.toString()}",
      ${permissionsGraphqlMutation()(data)}
    }) {
      createdDate
    }
  }`
  return request(`${process.env.GATSBY_API_URL}/graphql`, mutation)
}

class FormAskQuestion extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      submitSuccess: false,
      submitError: false,
      errorMessage: 'Please try again later.'
    }
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  async handleSubmit(formData) {
    const type = document.location.pathname.includes('cars') ? 'Car' : 'Van'
    trackEvent(`New${type}AskQuestion`, 'Form', 'Submit', '', {
      enquiryType: formData.enquiryType,
      location: this.props.location
    })
    try {
      const response = await submitForm({
        ...formData,
        location: this.props.location,
        registration: this.props.registration
      })
      if (response && response.createQuestion) {
        this.setState({ submitSuccess: true })
      } else {
        trackEvent('AskQuestion', 'Form', 'Error')
        this.setState({ submitError: true })
      }
    } catch (e) {
      trackEvent('AskQuestion', 'Form', 'Error')
      this.setState({ submitError: true })
    }
  }

  render() {
    if (this.state.submitSuccess) {
      trackEvent('AskQuestion', 'Form', 'Thanks')
      return (
        <div>
          <Heading as="h3">Thank you!</Heading>
          <Text>A member of our team will be in contact shortly.</Text>
        </div>
      )
    }

    const defaultFormValue = {
      enquiryType: enquiryTypes[0]
    }

    return (
      <Form
        schema={modelSchema}
        onSubmit={this.handleSubmit}
        className="f24form"
        data-f24name="ask-question-form"
        defaultValue={defaultFormValue || {}}
      >
        {this.state.submitError && (
          <div>
            <Heading as="h3">Sorry, there has been a problem.</Heading>
            <Text>{this.state.errorMessage}</Text>
          </div>
        )}
        <Fieldset>
          <Flex flexWrap="wrap" width={'100%'} py={[10]}>
            <Box width={[12 / 12, null, 5 / 12]} mr={[0, null, '12%']} mb={10}>
              <Label>First name*</Label>
              <Input
                placeholder="Enter first name"
                name="firstName"
                aria-label="You are required enter your first name"
              />
              <Message for="firstName" />
            </Box>
            <Box width={[12 / 12, null, 5 / 12]} mb={10}>
              <Label>Last name*</Label>
              <Input
                placeholder="Enter last name"
                name="lastName"
                aria-label="You are required enter your last name"
              />
              <Message for="lastName" />
            </Box>
            <Box width={[12 / 12, null, 5 / 12]} mr={[0, null, '12%']} mb={10}>
              <Label>Phone number</Label>
              <Input placeholder="Enter phone number" name="phone" />
              <Message for="phone" />
            </Box>
            <Box width={[12 / 12, null, 5 / 12]} mb={10}>
              <Label>Postcode</Label>
              <Input placeholder="Enter postcode" name="postcode" />
              <Message for="postcode" />
            </Box>
            <Box width={[12 / 12, null, 12 / 12]} mb={10}>
              <Label>Email address*</Label>
              <Input
                placeholder="Enter email address"
                name="email"
                aria-label="You are required to enter your email"
              />
              <Message for="email" />
            </Box>

            <Box width={[12 / 12, null, 12 / 12]} mb={10}>
              <Label bg={this.props.bg}>Enquiry type</Label>
              <Widget>
                <Form.Field
                  bg={this.props.bg}
                  placeholder="Enquiry Type"
                  name="enquiryType"
                  data={enquiryTypes}
                  type="dropdownlist"
                  aria-label="Select the reason for your enquiry"
                />
              </Widget>
              <Message for="enquiryType" />
            </Box>

            <Box width={[12 / 12, null, 12 / 12]} mb={10}>
              <Label>My question</Label>
              <TextArea
                name="question"
                type="textarea"
                rows={3}
                placeholder="Ask us anything"
                aria-label="Enter your question"
              />
              <Message for="question" />
            </Box>
            <PermissionControls email sms phone />
            <Box width={12 / 12} mb={10}>
              <Label>
                <Checkbox
                  name="termsAndConditions"
                  aria-label="You are required to agree to our terms and conditions"
                />
                I agree to Citygate’s{' '}
                <Link to="/legal">terms and conditions</Link>
              </Label>
              <Message for="termsAndConditions" />
            </Box>
            <CenteredContentBox width={12 / 12} mt={35}>
              <SubmitButton type="submit" aria-label="Ask question">
                Ask question
              </SubmitButton>
            </CenteredContentBox>
          </Flex>
        </Fieldset>
      </Form>
    )
  }
}

export default FormAskQuestion
